import { useState, useRef, useEffect } from "react";
import api, { API_BASE } from "../lib/api";
import { Icon } from "./ui-kit";
import { AiBlocks } from "./AiBlocks";
import { cn } from "../lib/utils";
import { toast } from "sonner";

export default function AskAIPanel({ topicId, topicName, compact }) {
  const [messages, setMessages] = useState([]); // {role, text?/blocks?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [convId, setConvId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/ask-ai/suggestions").then(({ data }) => setSuggestions(data.suggestions)).catch(() => {});
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ask-ai", { message: q, topic_id: topicId, conversation_id: convId });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", blocks: data.blocks }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", blocks: [{ type: "callout", style: "warning", text: "I couldn't answer right now. Please try again." }] }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "audio.webm");
        try {
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) send(data.text);
          else toast.error("Didn't catch that. Try again.");
        } catch {
          toast.error("Voice transcription failed. Please type instead.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const speak = async (blocks, idx) => {
    if (speakingIdx === idx) { audioRef.current?.pause(); setSpeakingIdx(null); return; }
    const text = (blocks || []).map((b) => b.text || (b.items || []).join(". ")).filter(Boolean).join(". ");
    try {
      setSpeakingIdx(idx);
      const res = await fetch(`${API_BASE}/tts`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      if (!res.ok) throw new Error();
      const buf = await res.blob();
      const url = URL.createObjectURL(buf);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setSpeakingIdx(null);
      audio.play();
    } catch {
      setSpeakingIdx(null);
      toast.error("Couldn't play audio.");
    }
  };

  return (
    <div className={cn("flex flex-col", compact ? "h-[540px]" : "h-[calc(100vh-11rem)]")}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-1">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl bg-mim-purple/10 flex items-center justify-center mx-auto mb-3">
              <Icon name="Sparkles" className="w-7 h-7 text-mim-purple" />
            </div>
            <h4 className="text-lg font-bold text-mim-navy">Ask Mimi anything</h4>
            <p className="text-sm text-mim-muted mt-1">{topicName ? `Context: ${topicName}. Just say "explain this".` : "I understand your current topic automatically."}</p>
          </div>
        )}
        {messages.map((m, i) => m.role === "user" ? (
          <div key={i} className="flex justify-end animate-fade-up">
            <div className="bg-mim-blue text-white rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] text-sm">{m.text}</div>
          </div>
        ) : (
          <div key={i} className="flex gap-2.5 animate-fade-up">
            <div className="w-8 h-8 rounded-xl bg-mim-purple/10 flex items-center justify-center shrink-0 mt-0.5">
              <Icon name="Sparkles" className="w-4 h-4 text-mim-purple" />
            </div>
            <div className="flex-1 bg-white border border-mim-line rounded-2xl rounded-tl-md p-4 max-w-[92%]">
              <AiBlocks blocks={m.blocks} />
              <button onClick={() => speak(m.blocks, i)} data-testid={`tts-btn-${i}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-mim-purple hover:underline">
                <Icon name={speakingIdx === i ? "Square" : "Volume2"} className="w-3.5 h-3.5" />
                {speakingIdx === i ? "Stop" : "Listen"}
              </button>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-mim-purple/10 flex items-center justify-center shrink-0">
              <Icon name="Sparkles" className="w-4 h-4 text-mim-purple animate-pulse" />
            </div>
            <div className="bg-white border border-mim-line rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-1">
              {[0, 1, 2].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-mim-purple/60 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
              <span className="text-sm text-mim-muted ml-2">Thinking…</span>
            </div>
          </div>
        )}
      </div>

      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 py-3">
          {suggestions.map((s) => (
            <button key={s} data-testid={`suggestion-${s.slice(0, 10)}`} onClick={() => send(s)}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-mim-line hover:border-mim-purple/40 hover:text-mim-purple transition-colors text-mim-navy">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="pt-3 border-t border-mim-line mt-2">
        <div className="flex items-center gap-2">
          <button onClick={toggleRecord} disabled={transcribing} data-testid="voice-btn"
            className={cn("relative w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors",
              recording ? "bg-mim-purple text-white mic-pulse" : "bg-mim-purple/10 text-mim-purple hover:bg-mim-purple/20")}>
            <Icon name={transcribing ? "LoaderCircle" : "Mic"} className={cn("w-5 h-5", transcribing && "animate-spin")} />
          </button>
          <input data-testid="ask-ai-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Ask a question…"}
            className="flex-1 px-4 py-2.5 rounded-xl border border-mim-line focus:border-mim-purple focus:ring-2 focus:ring-mim-purple/15 outline-none text-sm" />
          <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="ask-ai-send"
            className="w-11 h-11 rounded-xl bg-mim-purple text-white flex items-center justify-center shrink-0 disabled:opacity-40 hover:bg-mim-purple/90 transition-colors">
            <Icon name="Send" className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
